import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import type {
  PaymentsStatusDto,
  StartOnboardingRequest,
  SetSlugRequest,
  AccountSessionDto,
  DonationDashboardSummaryDto,
  DonationFeedDto,
  DonationDetailDto,
  DonationStatusTab,
  NeedsAttentionDto,
  StripeBalanceDto,
  StripePayoutScheduleDto,
  StripePayoutsDto,
  RefundDonationResponseDto,
  RefundErrorCode,
  DonationConfigDto,
  SaveDonationConfigRequest,
  DonationLogoUploadUrlDto,
  DonationLogoConfirmedDto,
  DonationLogoConfirmRequest,
  DonationLogoUploadUrlRequest,
} from '@signalvote/shared';
import { donationLogoError, donationLogoDimensionError } from '@signalvote/shared';
import { readImageDimensions } from '@/lib/imageDimensions';
import { authService } from '../../services/authService';
import { apiJson } from './apiFetch';
import type { ApiErrorSpec } from './apiFetch';
import { queryKeys } from '../../lib/queryKeys';
import { useCampaignStore } from '../../stores';

const PAYMENTS_PATH = '/api/finance/payments';

// ============================================================================
// Query Hooks
// ============================================================================

export function usePaymentsStatus() {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.finance.payments.status(),
    queryFn: () =>
      apiJson<PaymentsStatusDto>(`${PAYMENTS_PATH}/status`, {
        error: 'Failed to fetch payments status',
      }),
    enabled: authService.isAuthenticated() && !!selectedCampaign,
  });
}

// The donation-page "Advanced settings" config — the saved custom suggested-amount presets (empty
// when none set). Read-only; the editor seeds its initial state from this and the live preview mirrors
// it. Campaign-scoped.
export function useDonationConfig(enabled = true) {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.finance.payments.donationConfig(),
    queryFn: () =>
      apiJson<DonationConfigDto>(`${PAYMENTS_PATH}/donation-config`, {
        error: 'Failed to load donation settings',
      }),
    enabled: enabled && authService.isAuthenticated() && !!selectedCampaign,
  });
}

// The post-onboarding donation dashboard summary — account health + donations
// this period + fees this period, all from our own DB (no live Stripe call, so it
// never blocks the deadline-critical health banner). The live balance/payouts
// panels are a separate hook.
export function useDonationSummary(enabled = true) {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.finance.payments.summary(),
    queryFn: () =>
      apiJson<DonationDashboardSummaryDto>(`${PAYMENTS_PATH}/summary`, {
        error: 'Failed to fetch donation summary',
      }),
    enabled: enabled && authService.isAuthenticated() && !!selectedCampaign,
  });
}

// The recent-donations panel — the first page of DB-sourced portal donations
// (donor / amount / date / derived status), newest first. Read-only, no live
// Stripe call.
export function useDonationsFeed(enabled = true) {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.finance.payments.donations(),
    queryFn: () =>
      apiJson<DonationFeedDto>(`${PAYMENTS_PATH}/donations`, {
        error: 'Failed to fetch recent donations',
      }),
    enabled: enabled && authService.isAuthenticated() && !!selectedCampaign,
  });
}

// The "View all" filterable feed — keyset-paginated portal donations for a status
// tab (all / succeeded / refunded / disputed). An infinite query: page one is the
// same first page the recent-donations card shows; "Load more" follows the opaque
// `nextCursor`. Read-only, DB-sourced (no live Stripe call). Per-tab query key so
// switching tabs is a distinct cached fetch.
export function useDonationsFeedPaged(tab: DonationStatusTab, enabled = true) {
  const { selectedCampaign } = useCampaignStore();

  return useInfiniteQuery({
    queryKey: queryKeys.finance.payments.donationsFeed(tab),
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams();
      // 'all' is the server default, so it rides as no ?status= param.
      if (tab !== 'all') params.set('status', tab);
      if (pageParam) params.set('cursor', pageParam);
      const qs = params.toString();
      return apiJson<DonationFeedDto>(`${PAYMENTS_PATH}/donations${qs ? `?${qs}` : ''}`, {
        error: 'Failed to fetch donations',
      });
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: enabled && authService.isAuthenticated() && !!selectedCampaign,
  });
}

// The per-donation detail drawer — the full record for one portal donation (donor +
// compliance fields + derived status). Enabled only when a row is selected; a 404
// (out-of-scope / unknown id) surfaces as `isError`. Read-only, DB-sourced.
export function useDonationDetail(contributionId: string | null) {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.finance.payments.donationDetail(contributionId ?? ''),
    queryFn: () =>
      apiJson<DonationDetailDto>(`${PAYMENTS_PATH}/donations/${contributionId}`, {
        error: 'Failed to fetch the donation',
      }),
    enabled:
      !!contributionId && authService.isAuthenticated() && !!selectedCampaign,
  });
}

// The needs-attention card — open disputes/chargebacks on portal donations,
// read-only. Empty `disputes` means nothing to surface (the card hides).
export function useNeedsAttention(enabled = true) {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.finance.payments.needsAttention(),
    queryFn: () =>
      apiJson<NeedsAttentionDto>(`${PAYMENTS_PATH}/needs-attention`, {
        error: 'Failed to fetch donations needing attention',
      }),
    enabled: enabled && authService.isAuthenticated() && !!selectedCampaign,
  });
}

// The "available to pay out" panel — a LIVE connected-account balance read (never
// our DB, never persisted per ADR-011). Separate from the DB summary so the page and
// the deadline-critical health banner never block on Stripe; a failure surfaces as
// `isError` → an inline retry in the panel.
export function useStripeBalance(enabled = true) {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.finance.payments.balance(),
    queryFn: () =>
      apiJson<StripeBalanceDto>(`${PAYMENTS_PATH}/balance`, {
        error: 'Failed to fetch available balance',
      }),
    enabled: enabled && authService.isAuthenticated() && !!selectedCampaign,
  });
}

// The connected account's actual payout cadence from live Stripe Balance Settings.
// Read-only and independently retryable; callers never infer a fallback schedule.
export function useStripePayoutSchedule(enabled = true) {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.finance.payments.payoutSchedule(),
    queryFn: () =>
      apiJson<StripePayoutScheduleDto>(`${PAYMENTS_PATH}/payout-schedule`, {
        error: 'Failed to fetch payout schedule',
      }),
    enabled: enabled && authService.isAuthenticated() && !!selectedCampaign,
  });
}

// The payouts panel — LIVE connected-account payouts, newest first. An infinite query:
// the landing shows the first page (~20); "view all" pages full history by following
// Stripe's `starting_after` cursor (`nextCursor`). Shared with the next-payout hero
// (same key → one fetch). Live-only, never persisted (ADR-011).
export function useStripePayouts(enabled = true) {
  const { selectedCampaign } = useCampaignStore();

  return useInfiniteQuery({
    queryKey: queryKeys.finance.payments.payouts(),
    queryFn: async ({ pageParam }) => {
      const suffix = pageParam
        ? `?starting_after=${encodeURIComponent(pageParam)}`
        : '';
      return apiJson<StripePayoutsDto>(`${PAYMENTS_PATH}/payouts${suffix}`, {
        error: 'Failed to fetch payouts',
      });
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: enabled && authService.isAuthenticated() && !!selectedCampaign,
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

export function useStartOnboarding() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: StartOnboardingRequest) =>
      apiJson<PaymentsStatusDto>(`${PAYMENTS_PATH}/account`, {
        method: 'POST',
        json: data,
        error: (body) => new Error(body.error || 'Could not start setup'),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.finance.payments.status(),
      });
    },
  });
}

// Imperative mint of a Connect account session — the client secret is
// short-lived and single-use, so this is a mutation with NO cache entry. The
// page mints once to read the publishable key + first client secret, then hands
// `fetchClientSecret` (which re-mints) to the embedded component.
export function useCreateAccountSession() {
  return useMutation({
    mutationFn: () =>
      apiJson<AccountSessionDto>(`${PAYMENTS_PATH}/account-session`, {
        method: 'POST',
        error: (body) => new Error(body.error || 'Could not connect to our payments partner'),
      }),
  });
}

export function useSetDonationSlug() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SetSlugRequest) =>
      apiJson<PaymentsStatusDto>(`${PAYMENTS_PATH}/slug`, {
        method: 'PUT',
        json: data,
        // The server message is already treasurer-safe (e.g. "That link is
        // taken", "Locked once published"). Surface it verbatim.
        error: (body) => new Error(body.error || 'Could not save your link'),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.finance.payments.status(),
      });
    },
  });
}

/**
 * A refund failure carrying the server's stable `code` so the confirmation dialog can route
 * `insufficient_balance` (already paid out) to the support hatch and everything else to a generic
 * failure message. `code` is undefined for a network error or an unparseable body.
 */
export class RefundError extends Error {
  readonly code?: RefundErrorCode;
  constructor(message: string, code?: RefundErrorCode) {
    super(message);
    this.name = 'RefundError';
    this.code = code;
  }
}

// The donation dashboard's one write action: a full refund of a portal donation (Finance:Manage only —
// the button is hidden for others AND the endpoint enforces it). On success we invalidate the whole
// payments subtree so the feed status flips to Refunded, the detail refreshes, and the summary re-totals.
export function useRefundDonation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (contributionId: string) =>
      apiJson<RefundDonationResponseDto>(`${PAYMENTS_PATH}/donations/${contributionId}/refund`, {
        method: 'POST',
        error: (body) =>
          new RefundError(
            body.error || 'We couldn’t complete this refund.',
            body.code as RefundErrorCode | undefined,
          ),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.finance.payments.all(),
      });
    },
  });
}

// Save the donation-page "Advanced settings" (custom amount presets). Low-contention single-admin
// config write — last-write-wins, no concurrency token (matches the slug/publish siblings). On success
// we invalidate the config so the editor reconciles to the server-normalized values.
export function useSaveDonationConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SaveDonationConfigRequest) =>
      apiJson<DonationConfigDto>(`${PAYMENTS_PATH}/donation-config`, {
        method: 'PUT',
        json: data,
        // The server message is already treasurer-safe (validation bounds). Surface it verbatim.
        error: (body) => new Error(body.error || 'Could not save your donation settings'),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.finance.payments.donationConfig(),
      });
    },
  });
}

// Upload a campaign logo for the donation page. Orchestrates the presigned flow: validate client-side
// (mirrors the server bounds), mint a short-lived presigned PUT URL, then upload the binary DIRECTLY to
// object storage (prod) or to the API's local-upload endpoint (dev). Returns the VERIFIED descriptor so
// the caller stashes a storage key whose bytes the server has read back and approved, and shows its
// public URL as a preview.

/** Unwraps the API's `{ error }` envelope, falling back when the body is missing or not JSON. */
function donationLogoFailure(fallback: string): ApiErrorSpec {
  return (body) => new Error(body.error || fallback);
}

export function useUploadDonationLogo() {
  return useMutation({
    mutationFn: async (file: File): Promise<DonationLogoConfirmedDto> => {
      const clientError = donationLogoError({ type: file.type, size: file.size });
      if (clientError) throw new Error(clientError);

      // Measure before uploading. Bytes say nothing about what an image costs a donor's phone to
      // decode, and catching it here saves the admin a pointless round trip; the server re-checks the
      // stored object's real dimensions regardless.
      const dimensionError = donationLogoDimensionError(await readImageDimensions(file));
      if (dimensionError) throw new Error(dimensionError);

      const upload = await apiJson<DonationLogoUploadUrlDto>(
        `${PAYMENTS_PATH}/donation-logo/upload-url`,
        {
          method: 'POST',
          json: {
            fileName: file.name,
            contentType: file.type,
            sizeBytes: file.size,
          } satisfies DonationLogoUploadUrlRequest,
          error: donationLogoFailure('Could not prepare the logo upload'),
        },
      );

      // No ACL header on either path: the URL targets the staging area, whose bytes are not verified yet
      // and must not be publicly readable — the server applies public-read when it copies the verified
      // image out. Both calls are written out rather than picked into a variable, because `fetch` and
      // `authenticatedFetch` both lose their `this` when detached from their receiver.
      const putRequest: RequestInit = {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      };
      const put = upload.usesPresignedUrls
        // Direct-to-storage: auth rides in the presigned URL's query string, so an Authorization header
        // would be rejected as a signature mismatch — this one deliberately skips authenticatedFetch.
        ? await fetch(upload.uploadUrl, putRequest)
        // Local dev: the upload goes to the API, which needs the auth/tenant headers.
        : await authService.authenticatedFetch(upload.uploadUrl, putRequest);
      if (!put.ok) throw new Error('The logo upload failed. Please try again.');

      // Nothing so far proves what actually landed in storage: the presigned PUT constrains neither
      // length nor content, and every check up to here ran on this side of the wire. The server reads the
      // object back and, if it passes, copies it to a durable key this upload URL cannot reach — so the
      // key we preview and save is one whose bytes were verified and can no longer be swapped.
      return apiJson<DonationLogoConfirmedDto>(`${PAYMENTS_PATH}/donation-logo/confirm`, {
        method: 'POST',
        json: { storageKey: upload.storageKey } satisfies DonationLogoConfirmRequest,
        error: donationLogoFailure('That logo could not be verified. Please try a different image.'),
      });
    },
  });
}

export function usePublishDonationPage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      apiJson<PaymentsStatusDto>(`${PAYMENTS_PATH}/publish`, {
        method: 'POST',
        error: (body) => new Error(body.error || 'Could not publish your donation page'),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.finance.payments.status(),
      });
    },
  });
}
